import { useCallback } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Table, type Column } from 'grabbitbot-legacy-ui'
import { statisticsClient } from '../../api'
import type { ClosedPositionOfCoin, PositionPage } from '../../api/statisticsTypes'
import { formatNumberByCoin } from '../trading/coinConfigLabels'
import { formatDateTime } from '../trading/tradingConstants'
import { coinSheetLink, pairLabel } from './positionLinks'
import { useCursorPage } from './useCursorPage'
import { failureText } from '../../api/errorMessages'

function num(value: unknown): number {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

/**
 * Equivalent of legacy `StatisticsClosedPositionsComponent` (route
 * `.../statistics/positions/closed`, contract #11 `type=closed`). Legacy
 * grouped "Buy"/"Sell" columns under a second header row — the shared Table
 * component only has one header row, so those columns are prefixed instead.
 */
export function ClosedPositionsPage() {
  const { exchangeId = '', tradingType = 'spot' } = useParams<{ exchangeId: string; tradingType: string }>()
  if (tradingType !== 'spot') {
    return (
      <div className="portal-card">
        <h1>Closed positions</h1>
        <p className="portal-notice portal-notice--warning">Margin trading is not supported.</p>
      </div>
    )
  }
  return <SpotClosedPositions key={exchangeId} exchangeId={exchangeId} />
}

function SpotClosedPositions({ exchangeId }: { exchangeId: string }) {
  const fetcher = useCallback(
    (pageSize: number, start: string | undefined) =>
      statisticsClient.positions(exchangeId, 'closed', { pageSize, start }) as Promise<
        PositionPage<ClosedPositionOfCoin>
      >,
    [exchangeId],
  )
  const { items, loading, error, page, hasPrev, hasNext, next, prev, pageSize } = useCursorPage<ClosedPositionOfCoin>(
    fetcher,
    [exchangeId],
  )

  const columns: Column<ClosedPositionOfCoin>[] = [
    { key: 'openedAt', label: 'Open Date', align: 'left', sortValue: (r) => r.openedAt, format: (v) => formatDateTime(v as string) },
    { key: 'closedAt', label: 'Close Date', align: 'left', sortValue: (r) => r.closedAt, format: (v) => formatDateTime(v as string) },
    {
      key: 'coinPair',
      label: 'Coin pair',
      align: 'left',
      sortValue: (r) => pairLabel(r.coin1, r.coin2),
      format: (_v, r) => <Link to={coinSheetLink(exchangeId, r.coin1, r.coin2)}>{r.coinPair ?? pairLabel(r.coin1, r.coin2)}</Link>,
    },
    { key: 'bid', label: 'Bid', numeric: true, sortValue: (r) => num(r.bid), format: (_v, r) => `${formatNumberByCoin(num(r.bid), r.coin2)} ${r.coin2}` },
    { key: 'buyPrice', label: 'Buy Price', numeric: true, sortValue: (r) => num(r.buyPrice), format: (v) => num(v).toFixed(8) },
    {
      key: 'buyAmount',
      label: 'Buy Value (quote)',
      numeric: true,
      sortValue: (r) => num(r.buyAmount),
      format: (_v, r) => `${formatNumberByCoin(num(r.buyAmount), r.coin2)} ${r.coin2}`,
    },
    {
      key: 'buyCount',
      label: 'Buy Value (base)',
      numeric: true,
      sortValue: (r) => num(r.buyCount),
      format: (_v, r) => `${formatNumberByCoin(num(r.buyCount), r.coin1)} ${r.coin1}`,
    },
    { key: 'sellPrice', label: 'Sell Price', numeric: true, sortValue: (r) => num(r.sellPrice), format: (v) => num(v).toFixed(8) },
    {
      key: 'sellAmount',
      label: 'Sell Value (quote)',
      numeric: true,
      sortValue: (r) => num(r.sellAmount),
      format: (_v, r) => `${formatNumberByCoin(num(r.sellAmount), r.coin2)} ${r.coin2}`,
    },
    {
      key: 'sellCount',
      label: 'Sell Value (base)',
      numeric: true,
      sortValue: (r) => num(r.sellCount),
      format: (_v, r) => `${formatNumberByCoin(num(r.sellCount), r.coin1)} ${r.coin1}`,
    },
    {
      key: 'yield',
      label: 'Profit',
      numeric: true,
      sortValue: (r) => num(r.yield),
      cellClass: (_v, r) => (num(r.yield) < 0 ? 'portal-pnl-negative' : num(r.yield) > 0 ? 'portal-pnl-positive' : undefined),
      format: (_v, r) => `${formatNumberByCoin(num(r.yield), r.coin2)} ${r.coin2}`,
    },
  ]

  return (
    <div>
      <div className="portal-page-title">
        <h1>Last Closed Positions</h1>
      </div>
      <div className="portal-card">
        {loading && <p>Loading…</p>}
        {error !== null && <p role="alert">{failureText(error, 'Could not load closed positions')}</p>}
        {!loading && !error && items.length === 0 && <p>No Closed Positions</p>}
        {items.length > 0 && (
          <div className="portal-table-scroll">
            <Table columns={columns} rows={items} rowKey={(r) => r.id} />
          </div>
        )}
        <nav className="gbx-pager" aria-label="Pagination">
          <button type="button" disabled={!hasPrev} onClick={prev}>
            Previous
          </button>
          <span>
            Page {page} ({pageSize}/page)
          </span>
          <button type="button" disabled={!hasNext} onClick={next}>
            Next
          </button>
        </nav>
      </div>
    </div>
  )
}
